import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { jobApplicantApi, JobApplicantsListParams } from '../../services/jobApplicantApi';

export interface JobApplicant {
    id: number;
    name: string;
    email: string;
    phone: string;
    designation: string;
    experience: string;
    education: string;
    resume_url?: string;
    status: 'pending' | 'shortlisted' | 'rejected' | 'hired';
    created_at: string;
    updated_at: string;
}

export interface JobApplicantsState {
    applicants: JobApplicant[];
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    } | null;
    filters: {
        designation: string;
        status: string;
        query: string;
    };
    designations: string[];
    loading: boolean;
    error: string | null;
}

const initialState: JobApplicantsState = {
    applicants: [],
    pagination: null,
    filters: {
        designation: '',
        status: '',
        query: '',
    },
    designations: [],
    loading: false,
    error: null,
};

export const fetchJobApplicants = createAsyncThunk(
    'jobApplicants/fetchJobApplicants',
    async (params: JobApplicantsListParams = {}) => {
        const response = await jobApplicantApi.getJobApplicants(params);
        return response;
    }
);

export const fetchJobApplicant = createAsyncThunk(
    'jobApplicants/fetchJobApplicant',
    async (id: number) => {
        const response = await jobApplicantApi.getJobApplicant(id);
        return response;
    }
);

export const createJobApplicant = createAsyncThunk(
    'jobApplicants/createJobApplicant',
    async (applicantData: any) => {
        const response = await jobApplicantApi.createJobApplicant(applicantData);
        return response;
    }
);

export const updateJobApplicant = createAsyncThunk(
    'jobApplicants/updateJobApplicant',
    async ({ id, applicantData }: { id: number; applicantData: any }) => {
        const response = await jobApplicantApi.updateJobApplicant(id, applicantData);
        return response;
    }
);

export const deleteJobApplicant = createAsyncThunk(
    'jobApplicants/deleteJobApplicant',
    async (id: number) => {
        await jobApplicantApi.deleteJobApplicant(id);
        return id;
    }
);

export const updateJobApplicantStatus = createAsyncThunk(
    'jobApplicants/updateJobApplicantStatus',
    async ({ id, status }: { id: number; status: string }) => {
        const response = await jobApplicantApi.updateJobApplicantStatus(id, status);
        return response;
    }
);

export const fetchDesignations = createAsyncThunk(
    'jobApplicants/fetchDesignations',
    async () => {
        const response = await jobApplicantApi.getDesignations();
        return response;
    }
);

const jobApplicantSlice = createSlice({
    name: 'jobApplicants',
    initialState,
    reducers: {
        setFilters: (state, action) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearFilters: (state) => {
            state.filters = {
                designation: '',
                status: '',
                query: '',
            };
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchJobApplicants.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchJobApplicants.fulfilled, (state, action) => {
                state.loading = false;
                state.applicants = action.payload.applicants;
                state.pagination = action.payload.pagination;
            })
            .addCase(fetchJobApplicants.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch job applicants';
            })
            .addCase(createJobApplicant.fulfilled, (state, action) => {
                state.applicants.unshift(action.payload);
            })
            .addCase(updateJobApplicant.fulfilled, (state, action) => {
                const index = state.applicants.findIndex(app => app.id === action.payload.id);
                if (index !== -1) {
                    state.applicants[index] = action.payload;
                }
            })
            .addCase(deleteJobApplicant.fulfilled, (state, action) => {
                state.applicants = state.applicants.filter(app => app.id !== action.payload);
            })
            .addCase(updateJobApplicantStatus.fulfilled, (state, action) => {
                const index = state.applicants.findIndex(app => app.id === action.payload.id);
                if (index !== -1) {
                    state.applicants[index] = action.payload;
                }
            })
            .addCase(fetchDesignations.fulfilled, (state, action) => {
                state.designations = action.payload;
            });
    },
});

export const { setFilters, clearFilters, clearError } = jobApplicantSlice.actions;
export default jobApplicantSlice.reducer;
